import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from "react";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import {
  toggle_lease_reminder_status,
  update_lease_reminder
} from "../../../../utils/urls";

const EditReminder = ({
  reminder,
  isDialog = false,
  onSuccess,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reminderData, setReminderData] = useState({
    name: "",
    type: "standard",
    remindOn: {
      invoiceDate: true,
      dueDate: false,
      afterOverdue: {
        enabled: false,
        daily: false,
        days: []
      }
    },
    time: "09:00",
    notificationTypes: [],
    message: "",
    isActive: true
  });

  useEffect(() => {
    if (reminder) {
      setReminderData({
        name: reminder.name || "",
        type: reminder.type || "standard",
        remindOn: {
          invoiceDate: reminder.remindOn?.invoiceDate || true,
          dueDate: reminder.remindOn?.dueDate || false,
          afterOverdue: {
            enabled: reminder.remindOn?.afterOverdue?.enabled || false,
            daily: reminder.remindOn?.afterOverdue?.daily || false,
            days: reminder.remindOn?.afterOverdue?.days || []
          }
        },
        time: reminder.time || "09:00",
        notificationTypes: reminder.notificationTypes || [],
        message: reminder.message || "",
        isActive: reminder.isActive !== undefined ? reminder.isActive : true
      });
    }
  }, [reminder]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReminderData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNotificationTypeChange = (type) => {
    setReminderData(prev => {
      const currentTypes = [...prev.notificationTypes];
      const index = currentTypes.indexOf(type);

      if (index === -1) {
        currentTypes.push(type);
      } else {
        currentTypes.splice(index, 1);
      }

      return {
        ...prev,
        notificationTypes: currentTypes
      };
    });
  };

  const handleRemindOnChange = (field, value) => {
    setReminderData(prev => ({
      ...prev,
      remindOn: {
        ...prev.remindOn,
        [field]: typeof value === 'object' ? value : !!value
      }
    }));
  };

  const handleDailyOverdueChange = (value) => {
    setReminderData(prev => ({
      ...prev,
      remindOn: {
        ...prev.remindOn,
        afterOverdue: {
          ...prev.remindOn.afterOverdue,
          daily: value
        }
      }
    }));
  };

  const handleOverdueDaysChange = (day) => {
    setReminderData(prev => {
      const currentDays = [...prev.remindOn.afterOverdue.days];
      const index = currentDays.indexOf(day);

      if (index === -1) {
        currentDays.push(day);
      } else {
        currentDays.splice(index, 1);
      }

      return {
        ...prev,
        remindOn: {
          ...prev.remindOn,
          afterOverdue: {
            ...prev.remindOn.afterOverdue,
            days: currentDays.sort((a, b) => a - b)
          }
        }
      };
    });
  };

  const validateForm = () => {
    if (!reminderData.name?.trim()) {
      toastify("Reminder name is required", "error");
      return false;
    }

    if (!reminderData.notificationTypes.length) {
      toastify("At least one notification type is required", "error");
      return false;
    }

    const validTypes = reminderData.notificationTypes.every(type =>
      ['SMS', 'EMAIL'].includes(type.toUpperCase())
    );
    if (!validTypes) {
      toastify("Invalid notification types", "error");
      return false;
    }

    if (!reminderData.time.match(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/)) {
      toastify("Invalid time format", "error");
      return false;
    }

    if (reminderData.remindOn.afterOverdue.enabled &&
      !reminderData.remindOn.afterOverdue.daily &&
      !reminderData.remindOn.afterOverdue.days.length) {
      toastify("Either enable daily reminders or select at least one overdue reminder day", "error");
      return false;
    }

    // Check if at least one remind on option is selected
    if (!reminderData.remindOn.invoiceDate &&
      !reminderData.remindOn.dueDate &&
      !reminderData.remindOn.afterOverdue.enabled) {
      toastify("Select at least one reminder option", "error");
      return false;
    }

    if (reminderData.message && reminderData.message.length > 500) {
      toastify("Message cannot exceed 500 characters", "error");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    try {
      if (!validateForm() || isSubmitting) {
        return;
      }

      setIsSubmitting(true);
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        toastify("Facility ID not found", "error");
        return;
      }

      const formData = {
        name: reminderData.name.trim(),
        type: reminderData.type,
        remindOn: {
          invoiceDate: reminderData.remindOn.invoiceDate,
          dueDate: reminderData.remindOn.dueDate,
          afterOverdue: {
            enabled: reminderData.remindOn.afterOverdue.enabled,
            daily: reminderData.remindOn.afterOverdue.daily,
            days: reminderData.remindOn.afterOverdue.days
          }
        },
        time: reminderData.time,
        notificationTypes: reminderData.notificationTypes.map(type => type.toUpperCase()),
        message: reminderData.message.trim()
      };

      const response = await makeRequest2(
        `${update_lease_reminder}/${facilityId}/${reminder._id}`,
        "PUT",
        formData
      );

      if (response.success) {
        toastify("Reminder updated successfully", "success");
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toastify(response.error || "Failed to update reminder", "error");
      }
    } catch (error) {
      console.error("Error updating reminder:", error);
      toastify(error.message || "An unexpected error occurred", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        toastify("Facility ID not found", "error");
        return;
      }

      const response = await makeRequest2(
        `${toggle_lease_reminder_status}/${facilityId}/${reminder._id}`,
        "PATCH",
        { isActive: !reminderData.isActive }
      );

      if (response.success) {
        setReminderData(prev => ({
          ...prev,
          isActive: !prev.isActive
        }));
        toastify(
          `Reminder ${!reminderData.isActive ? 'activated' : 'deactivated'} successfully`,
          "success"
        );
      } else {
        toastify(response.error || "Failed to update reminder status", "error");
      }
    } catch (error) {
      console.error("Error toggling reminder status:", error);
      toastify(error.message || "An unexpected error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!reminder) {
    return <div>Loading...</div>;
  }

  return (
    <div className="card-body">
      {isDialog && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="mb-1">{reminder.name}</h6>
            <span className={`badge ${reminderData.isActive ? 'bg-success' : 'bg-secondary'}`}>
              {reminderData.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <Button
            type="button"
            className={`btn btn-sm ${reminderData.isActive ? 'btn-outline-danger' : 'btn-outline-success'}`}
            onClick={handleToggleStatus}
            loading={loading}
            disabled={loading || isSubmitting}
          >
            <i className={`ti ${reminderData.isActive ? 'ti-ban' : 'ti-check'} me-1`}></i>
            {reminderData.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </div>
      )}

      <div className="row">
        <div className="col-sm-6">
          <label className="form-label">Reminder Name *</label>
          <InputText
            className="form-control"
            name="name"
            value={reminderData.name}
            onChange={handleInputChange}
            placeholder="Enter reminder name"
            disabled={isSubmitting}
          />
        </div>

        <div className="col-sm-6">
          <label className="form-label">Reminder Type</label>
          <select
            className="form-control"
            name="type"
            value={reminderData.type}
            onChange={handleInputChange}
            disabled={isSubmitting}
          >
            <option value="standard">Standard</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        <div className="col-sm-6 mt-3">
          <label className="form-label">Notification Types *</label>
          <div className="d-flex gap-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={reminderData.notificationTypes.includes('SMS')}
                onChange={() => handleNotificationTypeChange('SMS')}
                id="smsCheckEdit"
                disabled={isSubmitting}
              />
              <label className="form-check-label" htmlFor="smsCheckEdit">
                SMS
              </label>
            </div>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={reminderData.notificationTypes.includes('EMAIL')}
                onChange={() => handleNotificationTypeChange('EMAIL')}
                id="emailCheckEdit"
                disabled={isSubmitting}
              />
              <label className="form-check-label" htmlFor="emailCheckEdit">
                Email
              </label>
            </div>
          </div>
        </div>

        <div className="col-sm-6 mt-3">
          <label className="form-label">Time *</label>
          <InputText
            type="time"
            className="form-control"
            name="time"
            value={reminderData.time}
            onChange={handleInputChange}
            disabled={isSubmitting}
          />
        </div>

        <div className="col-12 mt-4">
          <h6 className="mb-3">Remind On *</h6>
          <div className="row">
            <div className="col-md-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={reminderData.remindOn.invoiceDate}
                  onChange={(e) => handleRemindOnChange('invoiceDate', e.target.checked)}
                  id="invoiceDateCheckEdit"
                  disabled={isSubmitting}
                />
                <label className="form-check-label" htmlFor="invoiceDateCheckEdit">
                  Invoice Date
                </label>
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={reminderData.remindOn.dueDate}
                  onChange={(e) => handleRemindOnChange('dueDate', e.target.checked)}
                  id="dueDateCheckEdit"
                  disabled={isSubmitting}
                />
                <label className="form-check-label" htmlFor="dueDateCheckEdit">
                  Due Date
                </label>
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={reminderData.remindOn.afterOverdue.enabled}
                  onChange={(e) => handleRemindOnChange('afterOverdue', {
                    enabled: e.target.checked,
                    daily: reminderData.remindOn.afterOverdue.daily,
                    days: e.target.checked ? reminderData.remindOn.afterOverdue.days : []
                  })}
                  id="overdueCheckEdit"
                  disabled={isSubmitting}
                />
                <label className="form-check-label" htmlFor="overdueCheckEdit">
                  After Overdue
                </label>
              </div>
            </div>
          </div>
        </div>

        {reminderData.remindOn.afterOverdue.enabled && (
          <div className="col-12 mt-3">
            <div className="mb-3">
              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={reminderData.remindOn.afterOverdue.daily}
                  onChange={(e) => handleDailyOverdueChange(e.target.checked)}
                  id="dailyOverdueCheckEdit"
                  disabled={isSubmitting}
                />
                <label className="form-check-label" htmlFor="dailyOverdueCheckEdit">
                  <strong>Send reminders every day after due date</strong>
                </label>
              </div>
            </div>

            {!reminderData.remindOn.afterOverdue.daily && (
              <div>
                <label className="form-label">Specific overdue reminder days:</label>
                <div className="d-flex gap-3">
                  {[1, 3, 7].map(day => (
                    <div className="form-check" key={day}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={reminderData.remindOn.afterOverdue.days.includes(day)}
                        onChange={() => handleOverdueDaysChange(day)}
                        id={`day${day}CheckEdit`}
                        disabled={isSubmitting}
                      />
                      <label className="form-check-label" htmlFor={`day${day}CheckEdit`}>
                        {day} {day === 1 ? 'Day' : 'Days'}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="col-12 mt-3">
          <label className="form-label">Custom Message (Optional)</label>
          <textarea
            className="form-control"
            name="message"
            value={reminderData.message}
            onChange={handleInputChange}
            rows="3"
            maxLength="500"
            placeholder="Enter custom reminder message"
            disabled={isSubmitting}
          />
          <small className="text-muted">
            {reminderData.message.length}/500 characters
          </small>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-4">
        {onClose && (
          <Button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
        <Button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting || loading}
        >
          {isSubmitting ? "Updating..." : "Update Reminder"}
        </Button>
      </div>
    </div>
  );
};

export default EditReminder;